import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const MissionValues = () => {
  const coreValues = [
    {
      icon: "DollarSign",
      title: "Cost-Effective Solutions",
      description: "We believe quality healthcare technology should be accessible. Our strategic partnerships and efficient operations enable us to offer competitive pricing without compromising on quality or service excellence.",
      color: "primary",
      features: [
        "Competitive pricing strategies",
        "Flexible payment options",
        "Value-based partnerships",
        "Cost optimization consulting"
      ]
    },
    {
      icon: "Shield",
      title: "GDPMD Compliance",
      description: "Our unwavering commitment to Good Distribution Practice for Medical Devices ensures every product meets the highest safety and quality standards, protecting both healthcare providers and patients.",
      color: "success",
      features: [
        "100% GDPMD certified operations",
        "Rigorous quality control processes",
        "Comprehensive documentation",
        "Regular compliance audits"
      ]
    },
    {
      icon: "Heart",
      title: "Ethics, Transparency, Respect & Trust",
      description: "We build lasting relationships through ethical business practices, transparent communication, mutual respect, and unwavering trust with all our stakeholders in the healthcare ecosystem.",
      color: "accent",
      features: [
        "Ethical business practices",
        "Transparent pricing & processes",
        "Respectful partnerships",
        "Trust-based relationships"
      ]
    }
  ];

  return (
    <section className="py-20 lg:py-32 bg-gradient-to-br from-muted/30 via-background to-muted/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 bg-success/10 rounded-full px-4 py-2 mb-6">
            <Icon name="Target" size={16} color="var(--color-success)" />
            <span className="text-success font-semibold text-sm">Our Foundation</span>
          </div>
          <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-text-primary mb-6">
            Mission & Core Values
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto leading-relaxed">
            Our mission is to advance Malaysian healthcare by providing innovative, 
            cost-effective medical technology solutions while maintaining the highest standards of quality and service.
          </p>
        </motion.div>

        {/* Mission Statement */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="bg-gradient-to-r from-primary/10 via-accent/10 to-success/10 rounded-2xl p-8 lg:p-12 mb-16 border border-primary/20"
        >
          <div className="text-center">
            <div className="flex items-center justify-center w-16 h-16 bg-primary rounded-2xl mx-auto mb-6">
              <Icon name="Compass" size={32} color="white" strokeWidth={2} />
            </div>
            <h3 className="text-2xl lg:text-3xl font-bold text-text-primary mb-4">Our Mission</h3>
            <p className="text-lg lg:text-xl text-text-secondary leading-relaxed max-w-4xl mx-auto">
              "To be Malaysia's most trusted partner in advancing healthcare through innovative medical technology, 
              ensuring every healthcare professional has access to world-class endoscopic and surgical solutions 
              that improve patient outcomes and enhance quality of life."
            </p>
          </div>
        </motion.div>

        {/* Core Values */}
        <div className="space-y-12 lg:space-y-16">
          {coreValues?.map((value, index) => (
            <motion.div
              key={value?.title}
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              className={`grid lg:grid-cols-2 gap-8 lg:gap-12 items-center ${
                index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
              }`}
            >
              {/* Content */}
              <div className={`space-y-6 ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                <div className="flex items-center space-x-4">
                  <div className={`flex items-center justify-center w-14 h-14 bg-${value?.color}/10 rounded-xl`}>
                    <Icon name={value?.icon} size={28} color={`var(--color-${value?.color})`} strokeWidth={2} />
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-text-primary">
                    {value?.title}
                  </h3>
                </div>
                
                <p className="text-lg text-text-secondary leading-relaxed">
                  {value?.description}
                </p>

                <div className="space-y-3">
                  <h4 className="text-lg font-semibold text-text-primary">Key Features:</h4>
                  <ul className="space-y-2">
                    {value?.features?.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center space-x-3">
                        <Icon name="CheckCircle" size={16} color={`var(--color-${value?.color})`} />
                        <span className="text-text-secondary">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Visual Element */}
              <div className={`${index % 2 === 1 ? 'lg:col-start-1' : ''}`}>
                <div className={`relative bg-gradient-to-br from-${value?.color}/5 to-${value?.color}/10 rounded-2xl p-8 lg:p-12 border border-${value?.color}/20`}>
                  <div className="text-center">
                    <div className={`inline-flex items-center justify-center w-24 h-24 bg-${value?.color}/10 rounded-2xl mb-6 ring-8 ring-${value?.color}/5`}>
                      <Icon name={value?.icon} size={48} color={`var(--color-${value?.color})`} strokeWidth={1.5} />
                    </div>
                    
                    {/* Stats or Visual Elements */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center">
                        <div className={`text-2xl font-bold text-${value?.color} mb-1`}>
                          {index === 0 ? '30%' : index === 1 ? '100%' : '95%'}
                        </div>
                        <div className="text-xs text-text-secondary">
                          {index === 0 ? 'Cost Savings' : index === 1 ? 'Compliance Rate' : 'Trust Score'}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className={`text-2xl font-bold text-${value?.color} mb-1`}>
                          {index === 0 ? '500+' : index === 1 ? '12+' : '1000+'}
                        </div>
                        <div className="text-xs text-text-secondary">
                          {index === 0 ? 'Partners' : index === 1 ? 'Years' : 'Relationships'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Vision Statement */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-16 lg:mt-20 text-center"
        >
          <div className="bg-card border border-border rounded-2xl p-8 lg:p-12 shadow-medical">
            <div className="flex items-center justify-center w-16 h-16 bg-accent rounded-2xl mx-auto mb-6">
              <Icon name="Eye" size={32} color="white" strokeWidth={2} />
            </div>
            <h3 className="text-2xl lg:text-3xl font-bold text-text-primary mb-4">Our Vision</h3>
            <p className="text-lg lg:text-xl text-text-secondary leading-relaxed max-w-4xl mx-auto mb-8">
              "To transform Malaysian healthcare by making cutting-edge medical technology accessible to every healthcare provider, 
              fostering a future where advanced endoscopic and surgical solutions are the standard of care nationwide."
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/product-portfolio"
                className="inline-flex items-center justify-center px-6 py-3 bg-primary text-white font-semibold rounded-lg hover:bg-primary/90 transition-colors duration-200"
              >
                <Icon name="Package" size={20} className="mr-2" />
                Explore Solutions
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-6 py-3 bg-transparent border border-accent text-accent font-semibold rounded-lg hover:bg-accent/5 transition-colors duration-200"
              >
                <Icon name="MessageCircle" size={20} className="mr-2" />
                Partner With Us
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default MissionValues;